import React, { useState, useEffect, useRef } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
  DragStartEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';

// Import types and utilities
import { DragDropContainerType, DragDropElementWrapper } from './index';
import { DragOverlayComponent } from './DnDKitDragOverlay';
import { ContainerRenderer } from './DnDKitContainer';
import { getSortableComponent, customCollisionDetection } from './DnDKitUtils';

// Re-export types for backward compatibility
export { DragDropContainerType };

interface DnDKitDragDropProps<T = unknown> {
  items: DragDropElementWrapper<T>[];
  onReorder: (newItems: DragDropElementWrapper<T>[]) => void;
  tableHeadContent?: React.ReactNode;
  className?: string;
  iconName?: string;
  dense?: boolean;
  type?: DragDropContainerType;
  usePortal?: boolean;
}

export const DnDKitDragDrop = <T = unknown,>({
  items,
  onReorder,
  className,
  dense = false,
  iconName,
  tableHeadContent,
  type = DragDropContainerType.LIST,
  usePortal = false,
}: DnDKitDragDropProps<T>) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);
  const [optimisticItems, setOptimisticItems] = useState<DragDropElementWrapper<T>[]>(items || []);
  const isReorderingRef = useRef(false);

  // Handle null or undefined items
  const safeItems = items || [];

  // Sync optimistic items with actual items when not reordering
  useEffect(() => {
    if (!isReorderingRef.current) {
      setOptimisticItems(safeItems);
    }
  }, [items]);

  const activeItem = optimisticItems.find((item) => item.id === activeId);
  const SortableComponent = getSortableComponent(type);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
    isReorderingRef.current = true;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    setActiveId(null);

    if (active.id !== over?.id && over) {
      const activeIndex = optimisticItems.findIndex((item) => item.id === active.id);
      const overIndex = optimisticItems.findIndex((item) => item.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        const newItems = arrayMove(optimisticItems, activeIndex, overIndex);
        // Update both states at once
        setOptimisticItems(newItems);
        onReorder(newItems);

        // Keep the flag for a bit to prevent sync issues
        setTimeout(() => {
          isReorderingRef.current = false;
        }, 50);
      }
    } else {
      isReorderingRef.current = false;
    }
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={customCollisionDetection}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={optimisticItems.map((item) => item.id || '')}
        strategy={verticalListSortingStrategy}
      >
        <ContainerRenderer
          type={type}
          items={optimisticItems}
          className={className}
          dense={dense}
          activeId={activeId}
          iconName={iconName}
          tableHeadContent={tableHeadContent}
          SortableComponent={SortableComponent}
        />
      </SortableContext>

      {activeItem && (
        <DragOverlayComponent
          iconName={iconName}
          activeItem={activeItem}
          type={type}
          SortableComponent={SortableComponent}
          usePortal={usePortal}
        />
      )}
    </DndContext>
  );
};

// Helper function to convert existing data format to sortable items
export const convertToSortableItems = <T = unknown,>(
  originalItems: T[],
  itemMapper: (
    item: T,
    index: number,
  ) => Omit<DragDropElementWrapper<T>, 'id' | 'dragAndDroppable'> & {
    id?: string;
    dragAndDroppable?: boolean;
  },
): DragDropElementWrapper<T>[] => {
  return originalItems.map((item, index) => {
    const mapped = itemMapper(item, index);
    return {
      id: mapped.id || `item-${index}`,
      dragAndDroppable: mapped.dragAndDroppable !== false,
      ...mapped,
    };
  });
};
